import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { darkModeAware, withStyles } from '@glu/theming';

import CheckboxRadio from '../CheckboxRadio/CheckboxRadio';
import LabelText from '../LabelText/LabelText';
import styles from './CheckboxRadioGroup.styles';

const propTypes = {
  /** JSS classes for styling */
  classes: PropTypes.objectOf(PropTypes.string),
  /** Class name to apply to container  */
  className: PropTypes.string,
  /** Is dark mode */
  dark: PropTypes.bool,
  /** Label text for group */
  groupLabel: PropTypes.string,
  /** Class name to apply to group label legend element  */
  groupLabelClass: PropTypes.string,
  /** defines if this is a list of checkboxes or a radio group */
  type: PropTypes.oneOf(['checkbox', 'radio']),
  /** defines if group items should be formatted horizontal (inline) or vertical (block) */
  format: PropTypes.oneOf(['inline', 'block']),
  /** items to render out */
  children: PropTypes.node,
  /** List of items to appear in group */
  itemList: PropTypes.arrayOf(PropTypes.shape({
    htmlId: PropTypes.string,
    label: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func
    // any other standard radio input attributes can be passed along
  })),
  /** Shows optional text on label */
  optional: PropTypes.bool
};

const defaultProps = {
  classes: {},
  className: '',
  dark: false,
  format: 'inline',
  type: 'checkbox',
  groupLabel: undefined,
  groupLabelClass: '',
  children: undefined,
  itemList: undefined,
  optional: false
};

const CheckboxRadioGroup = ({
  classes, className, dark, optional, format,
  groupLabel, groupLabelClass, children, itemList, type
}) => {
  const [statefulItemList, setStatefulItemList] = useState(itemList && itemList.map(item => ({
    checked: false,
    ...item
  })));

  const handleChange = (statefulName, name, value, itemCb = () => {}) => {
    const updatedList = statefulItemList && statefulItemList.map((item, idx) => {
      if (statefulName === `${item.name}-${idx}`) {
        return {
          ...item,
          checked: (type === 'radio') ? true : !!value
        };
      }
      return type === 'radio' ? { ...item, checked: false } : item;
    });
    setStatefulItemList(updatedList);
    itemCb(name, value);
  };

  return (
    <fieldset className={`${classes.root} ${className}`}>
      {groupLabel && (
        <legend className={groupLabelClass}>
          <LabelText label={groupLabel} optional={optional} />
        </legend>
      )}
      {children || statefulItemList.map((radioProps, idx) => {
        const statefulName = `${radioProps.name}-${idx}`;
        return (
          <div key={`CheckboxRadioGroup_item_${radioProps.htmlId}`} className={format === 'inline' ? classes.inline : classes.block}>
            <CheckboxRadio
              key={`CheckboxRadioGroup_item_${radioProps.htmlId}`}
              type={type}
              checked={radioProps.checked}
              dark={dark}
              className={classes.inlineBlock}
              {...radioProps}
              onChange={(name, value) => {
                handleChange(statefulName, name, value, radioProps.onChange);
              }}
            />
          </div>
        );
      })}
    </fieldset>
  );
};

CheckboxRadioGroup.propTypes = propTypes;
CheckboxRadioGroup.defaultProps = defaultProps;

export const BareCheckboxRadioGroup = CheckboxRadioGroup;
export default darkModeAware(withStyles(styles)(CheckboxRadioGroup));
